import {Injectable} from "@angular/core";
import {ClearanceStatus, EmergencyProcedureMutationEntry, GetAllEmergencyProcedureMutations} from "@portbase/bezoekschip-service-typescriptmodels";
import {sendQuery} from "../../../common/utils";
import {EmergencyProcedureService} from "../../../admin/emergencyprocedure/emergencyprocedure.service";
import * as XLSX from 'xlsx-js-style';
import {WorkSheet} from 'xlsx-js-style';
import clearanceAttentionPoints from '@portbase/bezoekschip-service-typescriptmodels/refdata/clearance-attention-points.json';

const HEADER = ['CRN', 'Timestamp', 'User', 'Berth Visit Id', 'Auto Synced to HaMIS', 'Field', 'Value', 'Remark', 'Attention Points'];
const VALUE_COL_INDEX = HEADER.indexOf('Value');

/**
 * A single exported field. `remark` and `attentionPoints` are only populated for
 * clearance rows; for every other field they stay undefined so their columns are blank.
 * `autoSynced` reflects whether the field is auto-synced to HaMIS; it defaults to true
 * for every field except the clearances.
 */
interface ExportField {
  field: string;
  value: any;
  remark?: string;
  attentionPoints?: string;
  autoSynced?: boolean;
}

// The five port-authority clearances, with the label shown in the export.
const CLEARANCE_FIELDS: { key: string; label: string }[] = [
  {key: 'ekh', label: 'Clearance Visit'},
  {key: 'mdoh', label: 'Clearance Health'},
  {key: 'sspi', label: 'Clearance Security'},
  {key: 'emas', label: 'Clearance Waste'},
  {key: 'emgs', label: 'Clearance Dangerous Goods'},
];

@Injectable({providedIn: 'root'})
export class EmergencyProcedureExportService {

  constructor(private emergencyProcedureService: EmergencyProcedureService) {}

  export(): void {
    const portUnCode = this.getActivePortUnCode();

    sendQuery<EmergencyProcedureMutationEntry[]>(
      'com.portbase.bezoekschip.common.api.emergencyprocedure.GetAllEmergencyProcedureMutations',
      <GetAllEmergencyProcedureMutations>{ portUnCode },
      { showSpinner: true }
    ).subscribe(entries => {
      const data = this.buildExcelData(entries);
      this.writeExcelFile(data);
    });
  }

  private getActivePortUnCode(): string | undefined {
    return this.emergencyProcedureService.emergencyProceduresResource.value()
      .find(ep => ep.enabled)?.portUnCode;
  }

  private buildExcelData(entries: EmergencyProcedureMutationEntry[]): any[][] {
    const data: any[][] = [HEADER];

    entries.forEach(entry => {
      const rows = this.buildRowsForEntry(entry);
      rows.forEach(row => data.push(row));
    });

    return data;
  }

  private buildRowsForEntry(entry: EmergencyProcedureMutationEntry): any[][] {
    const commonCols = [
      entry.crn,
      entry.timestamp,
      entry.userFullName,
      // TODO: Add Movement Type column once it can be derived from the mutation model again
      entry.movementTarget?.berthVisitId,
    ];

    return this.collectExportFields(entry)
      .map(({field, value, remark, attentionPoints, autoSynced}) =>
        [...commonCols, (autoSynced ?? true) ? 'Yes' : 'No', field, value, remark ?? '', attentionPoints ?? '']);
  }

  private writeExcelFile(data: any[][]): void {
    const workBook = XLSX.utils.book_new();
    const workSheet = XLSX.utils.aoa_to_sheet(data);

    this.formatValueCells(workSheet, data.length);
    this.applyCellStyles(workSheet, data.length);
    this.applyAutoColumnWidths(workSheet, data);

    XLSX.utils.book_append_sheet(workBook, workSheet);
    XLSX.writeFile(workBook, 'emergency-procedure-mutations.xlsx', { cellDates: true });
  }

  // Matches ISO-8601 date-times like "2025-06-15T09:30:00Z" or with an offset/millis.
  private static readonly ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
  private static readonly EXCEL_DATE_TIME_FORMAT = 'yyyy-mm-dd hh:mm';

  /**
   * Normalises the Value column after the sheet is built:
   * - booleans become the text "Yes"/"No";
   * - ISO date-time strings become real Excel date cells with date/time formatting,
   *   so Excel treats them as dates (sortable, filterable) rather than plain text.
   */
  private formatValueCells(workSheet: WorkSheet, rowCount: number): void {
    for (let row = 1; row < rowCount; row++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: VALUE_COL_INDEX });
      const cell = workSheet[cellRef];
      if (!cell) {
        continue;
      }

      if (typeof cell.v === 'boolean') {
        cell.t = 's';
        cell.v = cell.v ? 'Yes' : 'No';
        continue;
      }

      if (typeof cell.v === 'string' && EmergencyProcedureExportService.ISO_DATE_TIME.test(cell.v)) {
        const parsed = new Date(cell.v);
        if (!isNaN(parsed.getTime())) {
          cell.t = 'd';
          cell.v = parsed;
          cell.z = EmergencyProcedureExportService.EXCEL_DATE_TIME_FORMAT;
        }
      }
    }
  }

  /**
   * Styles every cell in one pass. All cells get top vertical alignment; on top of
   * that the header row is bold, the Value column is right-aligned, and the Attention
   * Points column wraps its multi-line content.
   */
  private applyCellStyles(workSheet: WorkSheet, rowCount: number): void {
    const attentionPointsCol = HEADER.indexOf('Attention Points');

    for (let row = 0; row < rowCount; row++) {
      for (let col = 0; col < HEADER.length; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
        if (!workSheet[cellRef]) {
          continue;
        }

        const alignment: XLSX.CellStyle['alignment'] = { vertical: 'top' };
        if (col === VALUE_COL_INDEX) {
          alignment.horizontal = 'right';
        }
        if (col === attentionPointsCol) {
          alignment.wrapText = true;
        }

        workSheet[cellRef].s = {
          alignment,
          ...(row === 0 ? { font: { bold: true } } : {}),
        };
      }
    }
  }

  private applyAutoColumnWidths(workSheet: WorkSheet, data: any[][]): void {
    workSheet['!cols'] = HEADER.map((_, colIndex) => {
      const maxWidth = data.reduce((max, row) => {
        const cellValue = row[colIndex] != null ? String(row[colIndex]) : '';
        // Multi-line cells (attention points) are sized by their widest line, not total length.
        const longestLine = cellValue.split('\n').reduce((m, line) => Math.max(m, line.length), 0);
        return Math.max(max, longestLine);
      }, 0);
      return { wch: maxWidth + 2 };
    });
  }

  private collectExportFields(entry: EmergencyProcedureMutationEntry): ExportField[] {
    const pairs: ExportField[] = [];
    const mutations = entry.mutation;
    if (!mutations) return pairs;

    this.collectPortFields(pairs, mutations.portVisit);
    this.collectBerthFields(pairs, mutations.berth);
    this.collectPortAuthorityFields(pairs, mutations.portAuthority);
    this.collectServicesFields(pairs, mutations.services);

    return pairs;
  }

  private collectPortFields(pairs: ExportField[], port: EmergencyProcedureMutationEntry['mutation']['portVisit']): void {
    if (!port) return;
    this.addIfPresent(pairs, 'ETA PBP', port.etaPbp);
    this.addIfPresent(pairs, 'PBP', port.pbp);
    this.addIfPresent(pairs, 'ETD Port', port.etdPort);
    this.addIfPresent(pairs, 'ATD Port', port.atdPort);
  }

  private collectBerthFields(pairs: ExportField[], berth: EmergencyProcedureMutationEntry['mutation']['berth']): void {
    if (!berth) return;
    this.addIfPresent(pairs, 'Berth', berth.code);
    this.addIfPresent(pairs, 'ETA', berth.eta);
    this.addIfPresent(pairs, 'ETD', berth.etd);
    this.addIfPresent(pairs, 'ATA', berth.ata);
    this.addIfPresent(pairs, 'ATD', berth.atd);
    this.addIfPresent(pairs, 'Bollard From', berth.bollardFrom);
    this.addIfPresent(pairs, 'Bollard To', berth.bollardTo);
    this.addIfPresent(pairs, 'Mooring Position', berth.mooringPosition);
  }

  private collectPortAuthorityFields(pairs: ExportField[], pa: EmergencyProcedureMutationEntry['mutation']['portAuthority']): void {
    if (!pa) return;
    this.collectClearanceFields(pairs, pa);
    this.addIfPresent(pairs, 'Actual Vessel Draft', pa.actualVesselDraft);
    this.addIfPresent(pairs, 'On Hold', pa.onHold);
    this.addIfPresent(pairs, 'On Hold Date', pa.onHoldDate);
    this.addIfPresent(pairs, 'Operational Contact', pa.operationalContact);
    this.addIfPresent(pairs, 'Pilot From Distance', pa.pilotFromDistance);
    this.addIfPresent(pairs, 'Pilot From Distance Accepted', pa.pilotFromDistanceAccepted);
    this.addIfPresent(pairs, 'Tidal Window', pa.tidalWindow);
    this.addIfPresent(pairs, 'Tidal Window Start', pa.tidalWindowStart);
    this.addIfPresent(pairs, 'Tidal Window End', pa.tidalWindowEnd);
  }

  /**
   * Emits one row per changed clearance. The status is the row's Value; the clearance
   * remark goes in the Remark column (no longer its own row); attention points are
   * listed in the Attention Points column as "type: value", one per line.
   *
   * `clearances` isn't part of the typed PortAuthorityMutation yet, so it's read dynamically.
   */
  private collectClearanceFields(pairs: ExportField[], pa: EmergencyProcedureMutationEntry['mutation']['portAuthority']): void {
    const clearances = (pa as any)?.clearances as Record<string, ClearanceMutationValue> | undefined;
    if (!clearances) return;

    for (const {key, label} of CLEARANCE_FIELDS) {
      const clearance = clearances[key];
      if (!clearance || clearance.status == null) {
        continue;
      }
      pairs.push({
        field: label,
        value: this.clearanceStatusLabel(clearance.status),
        remark: clearance.remarks ?? '',
        attentionPoints: this.formatAttentionPoints(key, clearance.attentionPoints),
        autoSynced: false,
      });
    }
  }

  /**
   * Maps a stored clearance status to the same label the editor form shows.
   * The backend persists "Approved with remarks" as ACTIONS_REQUIRED, so it is
   * folded back onto APPROVED_WITH_REMARKS here to match the form's vocabulary.
   */
  private clearanceStatusLabel(status: string): string {
    const normalised = status === 'ACTIONS_REQUIRED' ? ClearanceStatus.APPROVED_WITH_REMARKS : status;
    switch (normalised) {
      case ClearanceStatus.APPROVED:
        return 'Approved';
      case ClearanceStatus.APPROVED_WITH_REMARKS:
        return 'Accepted with conditions';
      case ClearanceStatus.REJECTED:
        return 'Rejected';
      case ClearanceStatus.PENDING:
        return 'Nothing filled in';
      case ClearanceStatus.UNKNOWN:
        return 'Unknown';
      default:
        return status;
    }
  }

  private formatAttentionPoints(clearanceKey: string, attentionPoints: AttentionPointValue[] | undefined): string {
    if (!attentionPoints?.length) {
      return '';
    }
    return attentionPoints
      .filter(ap => !!ap?.type)
      .map(ap => `${this.attentionPointLabel(clearanceKey, ap.type)}: ${ap.remarks ?? ''}`)
      .join('\n');
  }

  private attentionPointLabel(clearanceKey: string, type: string): string {
    const options = (clearanceAttentionPoints as Record<string, { type: string; description: string }[]>)[clearanceKey] ?? [];
    return options.find(o => o.type === type)?.description ?? type;
  }

  private collectServicesFields(pairs: ExportField[], services: EmergencyProcedureMutationEntry['mutation']['services']): void {
    if (!services) return;
    this.addIfPresent(pairs, 'Pilot', services.pilot);
    this.addIfPresent(pairs, 'Pilot Approval', services.pilotApproval);
    this.addIfPresent(pairs, 'Tugboat', services.tugboat);
    this.addIfPresent(pairs, 'Tugboat Organisation', services.tugboatOrganisation);
    this.addIfPresent(pairs, 'Number of Tugboats', services.numberOfTugboats);
    this.addIfPresent(pairs, 'Boatmen', services.boatmen);
    this.addIfPresent(pairs, 'Boatmen Organisation', services.boatmenOrganisation);
  }

  private addIfPresent(pairs: ExportField[], field: string, value: any): void {
    if (value != null) {
      pairs.push({field, value});
    }
  }
}

interface ClearanceMutationValue {
  status?: string;
  remarks?: string;
  attentionPoints?: AttentionPointValue[];
}

interface AttentionPointValue {
  type?: string;
  remarks?: string;
}
